/**
 * The Team button in every app's top bar, and the panel it opens.
 *
 * Users used to live only in My, so changing a cashier's PIN or switching
 * someone off from Cashier, Frame or Ping meant leaving the app for another
 * one — on a phone, several taps and a full page load for a ten-second job.
 * This brings the quick part of that page to wherever the manager already is.
 *
 * Shown only to people who can see the team, and never inside My, which has
 * the full page in its own sidebar.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Users } from 'lucide-react'
import { Button } from '../ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { usePermissions } from '../../hooks/usePermissions'
import TeamList from './TeamList'

const MY_ORIGIN = (((import.meta as any).env?.VITE_MY_URL as string) || '').replace(/\/$/, '')
const POS_ORIGIN = (((import.meta as any).env?.VITE_POS_URL as string) || '').replace(/\/$/, '')
const HERE = typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : ''
const IS_MY_APP = !!MY_ORIGIN && MY_ORIGIN === HERE
// Same test the TopBar uses: no POS URL configured means this IS the POS app.
const IS_POS_APP = !POS_ORIGIN || POS_ORIGIN === HERE

export default function TeamButton() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const [open, setOpen] = useState(false)

  if (IS_MY_APP || !can('team:view')) return null

  return (
    <>
      <Button
        variant="ghost" size="icon"
        className="h-8 w-8 text-foreground/70 hover:text-foreground"
        onClick={() => setOpen(true)}
        title={t('team.title', 'Team')} aria-label={t('team.title', 'Team')}
        data-testid="team-button"
      >
        <Users className="h-4 w-4" />
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-4 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{t('team.title', 'Team')}</SheetTitle>
            <SheetDescription>
              {IS_POS_APP
                ? t('team.hintPos', 'Who can open the till, their PINs, and who is switched on.')
                : t('team.hint', 'Everyone in your organization, at a glance.')}
            </SheetDescription>
          </SheetHeader>
          {/* Mounted only while open, so the list is fresh every time. */}
          {open && <TeamList preset={IS_POS_APP ? 'till' : 'all'} offerAddCashier={IS_POS_APP} />}
        </SheetContent>
      </Sheet>
    </>
  )
}
